/*
 * timeOfDay.ts — 端末ローカル時刻ユーティリティ（P2「端末は付喪神」基盤・2026-06-21）
 *
 * 後続 Wave の「深夜連動」が使う純関数。付喪神は深夜に活発になる（世界観: 丑三つ時の気配）。
 * そのため夜／深夜の判定を提供し、後続が ambient 気配の強度や connection 演出の出やすさを
 * 時刻で微調整できるようにする。
 *
 * 設計上の肝:
 *  - 判定は **端末ローカル時刻**（プレイヤーの体感時刻）で行う。サーバ時刻や UTC ではない。
 *  - テスト容易性のため now を引数で受け取れる。未指定なら new Date()（Date.now の直呼びは避ける）。
 *  - 副作用なし・window 非依存（SSR/Node でそのまま動く）。
 */

/** getTimeOfDay の戻り値。hour は 0..23（端末ローカル）。 */
export interface TimeOfDay {
  /** 端末ローカルの時（0..23） */
  hour: number;
  /** 夜帯か（22:00〜翌4:59）。気配がうっすら濃くなる時間帯 */
  isNight: boolean;
  /** 深夜帯か（23:00〜翌3:59）。付喪神が最も活発になる「丑三つ時」を含む時間帯 */
  isDeepNight: boolean;
}

/**
 * 端末ローカル時刻から夜／深夜を判定する。
 *
 * 境界の根拠:
 *  - isNight   = hour >= 22 || hour < 5  … 22 時以降〜朝 5 時前。一般的な「夜」の体感。
 *  - isDeepNight = hour >= 23 || hour < 4 … 23 時以降〜朝 4 時前。丑三つ時（≒2〜3 時台）を内包し、
 *    付喪神＝古い器に宿る気配が最も濃くなる帯。isNight の真部分集合になるよう内側に取る。
 *
 * @param now 判定対象の時刻。テストでは固定 Date を渡す。未指定なら現在時刻。
 */
export function getTimeOfDay(now: Date = new Date()): TimeOfDay {
  const hour = now.getHours();
  const isNight = hour >= 22 || hour < 5;
  const isDeepNight = hour >= 23 || hour < 4;
  return { hour, isNight, isDeepNight };
}
